import { useMutation, useQueryClient } from '@tanstack/react-query'
import { TankValuesArrayType } from '../types/apiResponse/tankTypes'
import { postData } from '../utils'
import { reloadGroupsWithSchoolsAndTanks } from '../utils/loadStorage'

export type gradeDataType = {
  departureTankId: string
  batchId: string | null
  arrivalTankList: TankValuesArrayType
}

export type gradeResponseType = {
  departureSchoolId: string
  arrivalSchoolsId: string[]
}

// Helper to convert yyyyMMdd number to YYYY-MM-DD string
const formatDate = (dateNum: number): string => {
  const dateStr = String(dateNum)
  if (dateStr.length === 8) {
    // yyyyMMdd format (e.g., 20241209)
    const year = dateStr.substring(0, 4)
    const month = dateStr.substring(4, 6)
    const day = dateStr.substring(6, 8)
    return `${year}-${month}-${day}`
  }
  // Fallback: treat as timestamp
  const date = new Date(dateNum)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const gradeAPI = async (gradeDate: number, gradeData: gradeDataType, departureId: string) => {
  // Transform to new V2 API format
  const requestBody = {
    move_date: formatDate(gradeDate),
    grade_cases: [{
      departure_tank_data: {
        id: gradeData.departureTankId,
        subtracted_count: gradeData.arrivalTankList.reduce((sum, tank) => sum + (tank.count || 0), 0),
      },
      arrival_tank_datas: gradeData.arrivalTankList.map((tank) => ({
        id: tank.tankId,
        count: tank.count || 0,
        sample_weight: tank.sampleWeight || 0,
      })),
    }],
  }

  const response = await postData<gradeResponseType>('/tank/grade', requestBody)

  reloadGroupsWithSchoolsAndTanks()

  return response
}

export const useGrade = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      gradeDate,
      gradeData,
      departureId,
    }: {
      gradeDate: number
      gradeData: gradeDataType
      departureId: string
    }) => gradeAPI(gradeDate, gradeData, departureId),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tankInfo'] })
    },

    onError: (error) => {
      console.log('Grade API Error:', error)
    },
  })
}
